<template>
	<div class="initial-chart-section">
		<AppCard :title="getString('today')">
			<template v-slot:body>
				<div class="row initial-chart-container">
					<div class="col-9 initial-chart-col">
						<AppChart
							type="area"
							:series="dataSeries"
							:xaxis="xaxisValues"
							:xaxis-annotations="xaxisAnnotations"
							:gradient-colors="['#a30777', '#a30777']"
							:colors="dataSeriesColors"
							height="100%"
							full-width
							class="initial-chart"
							:formatter="noCurrency ? null : r => currencyStore.readableCurrencyValue(r, false, true, true)"
							:no-currency="noCurrency"
							badge-live
							:dark="userStore.darkMode"
						/>
					</div>
					<div class="col-3 initial-side-info">
						<UserInitialCardInfo
							v-for="(card, index) in cardsInfo"
							:key="index" blue :title="card.title"
							class="initial-side-info-square"
							:value="card.value"
							:last-period-value="card.previousValue"
							:symbol="card.symbol ? card.symbol : ''"
							:with-cents="card.hasOwnProperty('withCents') ? card.withCents : false"
							:no-currency="card.hasOwnProperty('noCurrency') ? card.noCurrency : false" />
					</div>
				</div>
			</template>
		</AppCard>
	</div>
</template>

<script>
import UserInitialCardInfo from "@/user/components/analytics/UserInitialCardInfo.vue";

import AppChart from "@/shared/components/AppChart.vue";
import AppCard from "@/shared/components/AppCard.vue";

import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import {useUserStore} from "@/user/stores";
export default {
	setup() {
		const currencyStore = useCurrencyStore();
		const languageStore = useLanguageStore();
		return {getString: languageStore.getString, userStore: useUserStore(), currencyStore, languageStore};
	},
	name: "SummaryCardBest",
	components: { AppChart, AppCard, UserInitialCardInfo },
	data() {
		return {
			timeSpans: [],
		};
	},
	props: {
		data: {
			type: Array,
			required: true,
			validator: (value) => {
				return value.every(d => {
					return d.name && d.data;
				});
			}
		},
		dataFieldValue: {
			type: String,
			default: null
		},
		cardsInfo: {
			type: Array,
			required: true,
			validator: (value) => {
				return value.every(card => {
					return card.hasOwnProperty("title") && card.hasOwnProperty("value") && card.hasOwnProperty("previousValue")
				});
			}
		},
		noCurrency: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		xaxisAnnotations() {
			let annotations = [];
			if(this.dayShift) {
				annotations = [
					{
						x: this.localeString(this.getLastShiftDatetime()),
						borderColor: 'rgba(7,82,163,0.7)',
						strokeDashArray: 3,
						label: {
							borderColor: 'transparent',
							orientation: 'horizontal',
							style: {
								background: 'transparent',
								color: 'transparent',
								padding: {
									left: 0,
									right: 0,
									top: 0,
									bottom: 0,
								}
							},
						}
					}
				];
			}
			return annotations;
		},
		dayShift() {
			return this.userStore && this.userStore.currBusiness && this.userStore.currBusiness.day_shift ? this.userStore.currBusiness.day_shift : '03:00:00';
		},
		xaxisValues() {
			return this.timeSpans.map(timeSpan => this.localeString(timeSpan));
		},
		dataSeries() {
			this.timeSpans = this.getHourlyTimeSpansForCurrentDay();
			let series = [];
			this.data.forEach(d => {
				series.push(
					{name: d.name, data: this.getDividedSeries(d.data)}
				)
			});
			if(series.length === 0) {
				series.push(
					{name: this.getString("no_data"), data: this.timeSpans.map(time => 0)}
				)
			}

			return series;
		},
		dataSeriesColors() {
			if(this.data.length > 0) {
				return this.data.map(d => d.color);
			} else {
				return ["#0752A3"];
			}
		},
	},
	methods: {
		localeString(date) {
			date = date ? new Date(date) : new Date();
			let options = {hour: '2-digit', minute:'2-digit', hour12: false};
			return date.toLocaleTimeString([this.languageStore.lang || 'en-US'], options);
		},
		getDividedSeries(data) {
			let series = [];
			let finalSpans = this.timeSpans.map(time => 0)
			let dayShift = this.getLastShiftDatetime();
			
			for(let i = 0; i < data.length; i++) {
				let parsedTime = data[i].date;
				let timeIndex = -1;
				for(let j = 0; j < this.timeSpans.length; j++) {
					if(parsedTime.getHours() === this.timeSpans[j].getHours() && 
					   parsedTime.getDate() === this.timeSpans[j].getDate()) {
						timeIndex = j;
						break;
					}
				}
				
				if(timeIndex >= 0 && timeIndex < finalSpans.length) {
					finalSpans[timeIndex] += this.dataFieldValue ? data[i][this.dataFieldValue] : 1;
				}
			}
			series = finalSpans;

			for(let i = 1; i < finalSpans.length; i++) {
				finalSpans[i] += finalSpans[i-1];
			}

			return series;
		},
		getHourlyTimeSpansForCurrentDay() {
			let dayShift = this.getLastShiftDatetime();
			let now = new Date();

			let endDate = new Date(now);
			if (endDate.getMinutes() > 0 || endDate.getSeconds() > 0) {
				endDate.setTime(endDate.getTime() + 3600 * 1000);
			}
			endDate.setMinutes(0, 0, 0);

			// Start from today's day shift time instead of 24 hours ago
			let startDate = new Date(dayShift);
			
			let dates = [];
			let iterDate = new Date(startDate);
			
			while (iterDate <= endDate) {
				dates.push(new Date(iterDate));
				iterDate.setTime(iterDate.getTime() + 3600 * 1000);
			}
			
			if (dayShift) {
				for (let i = 0; i < dates.length - 1; i++) {
					if (dates[i] <= dayShift && dates[i + 1] > dayShift) {
						if (dayShift.getMinutes() !== 0) {
							dates.splice(i + 1, 0, dayShift);
						}
						break;
					}
				}
			}
			
			return dates;
		},
		getLastShiftDatetime() {
			let splitShift = this.dayShift.split(':');
			let current = new Date();
			let dayShift = new Date();
			dayShift.setHours(splitShift[0] || 0, splitShift[1] || 0, splitShift[2] || 0, 0);
			if(dayShift > current) {
				dayShift.setDate(dayShift.getDate() - 1);
			}
			return dayShift;
		}
	},
	created() {
		this.timeSpans = this.getHourlyTimeSpansForCurrentDay();
	}
}
</script>

<style lang="scss" scoped>
.initial-chart-section {
	max-width: 1400px;
	margin: 0 auto;

	> .card-container {
		min-width: 100%;

		.initial-chart-container {
			align-items: stretch;
		}
		.chart-container {
			&:deep(.card-container) {
				box-shadow: none;

				.card-body {
					padding-top: 0;
					padding-bottom: 0;
				}
			}
			&:deep(.chart-svg-container) {
				min-height: 300px;
				padding-left: 10px;
			}
		}
		&:deep(.card-body) {
			padding-left: 0;
			padding-bottom: 0;
			padding-top: 0;

			.initial-chart-col {
				justify-content: center;
				width: 75%;
				max-width: calc(100% - 250px);
				min-width: calc(100% - 300px);
			}
			.initial-side-info {
				min-width: 250px;
				max-width: 300px;
				padding-bottom: 12px;
				padding-top: 12px;

				.initial-side-info-square {
					&:not(:last-child) {
						margin-bottom: 12px;
					}
				}
			}

			@media (max-width: 1080px) {
				.chart-container {
					padding-top: 8px;
				}
				.initial-chart-col {
					width: 100%;
					max-width: 100%;
					min-width: 100%;
				}
				.initial-side-info {
					flex-direction: row;
					justify-content: space-between;
					align-items: flex-start;
					min-width: 100%;
					max-width: 100%;
					padding-bottom: 0;

					.initial-side-info-square {
						height: unset;
						margin-left: 12px;
						margin-bottom: 12px;
					}
				}
			}

			@media (max-width: 960px) {
				.initial-side-info {
					flex-wrap: wrap;
					padding-top: 0;

					.initial-side-info-square {
						width: calc(50% - 12px);
						margin-left: 12px;
						margin-bottom: 12px;
					}
				}
			}

			@media (max-width: 768px) {
				.initial-side-info {

					.initial-side-info-square {
						width: calc(50% - 8px);
						margin-left: 8px;
						margin-bottom: 8px;
						&:not(:last-child) {
							margin-bottom: 8px;
						}
					}
				}
			}

			@media (max-width: 460px) {
				.initial-side-info {
					justify-content: center;

					.initial-side-info-square {
						width: calc(100% - 8px);
					}
				}
			}

		}
	}
}
</style>